<!-- Physical Results Page -->
<script setup lang="ts">
import { useStore } from '@/stores/main';
import { FmsType } from '@/types';
import { computed, inject, onMounted, ref, onUnmounted, watch } from 'vue';

const categoryId = 97;

const store = useStore();
const fmsType = inject('fms-type', FmsType.Personal); // Provide default value

const questionsAnswered = computed(() => {
  // Safely check if profile values exist
  const profileValues = store.profile?.values;
  if (!profileValues) return false;
  
  return (
    store.getProfileValue('energy') !== null &&
    store.getProfileValue('training') !== null &&
    store.getProfileValue('sitting') !== null
  );
});

const factor = computed(() => {
  if (!store.profile?.factors) return null;
  return store.profile.factors[categoryId];
});

async function handleImprove(value: string) {
  try {
    await store.setImproveValue(categoryId, value);
  } catch (error) {
    console.error('Error setting improve value:', error);
  }
}

const colorScheme = inject('colorScheme', 'green');
const borderColor = computed(() => {
  return (
    {
      purple: 'border-purple-light',
      yellow: 'border-yellow-light',
      green: 'border-green-light',
      blue: 'border-blue-light',
    }[colorScheme] ?? 'border-green-light'
  );
});

const improve = computed(() => {
  return factor.value?.improve || null;
});

const sectionResults = ref([]);

const fetchSectionResults = () => {
  try {
    const storedResults = localStorage.getItem('sectionResults');
    if (storedResults) {
      sectionResults.value = JSON.parse(storedResults);
    }
  } catch (error) {
    console.error('Error fetching section results:', error);
    sectionResults.value = [];
  }
};

const currentValue = ref('');
let intervalId;

onMounted(() => {
  fetchSectionResults();

  // Use window.setInterval with number result
  intervalId = window.setInterval(fetchSectionResults, 2000);

  // Set up watcher
  watch(
    sectionResults,
    newResults => {
      try {
        if (!Array.isArray(newResults)) return;
        
        newResults.forEach((section) => {
          if (!section || section.pageName !== 'physical_questions') return;
          
          const resultFactor = section.factors?.find(
            (factor) => factor?.name === 'Resultat Fysisk Aktivitet'
          );
          
          if (resultFactor && resultFactor.value !== undefined) {
            currentValue.value = String(resultFactor.value);
          }
        });
      } catch (error) {
        console.error('Error processing section results:', error);
      }
    },
    { immediate: true }
  );
});

onUnmounted(() => {
  if (intervalId !== undefined) {
    clearInterval(intervalId);
  }
});

const bgColors = [
  'bg-white',
  'bg-red-400',
  'bg-orange-400',
  'bg-amber-300',
  'bg-lime-300',
  'bg-green-400',
];

const highlightSwatch = (index: number) => {
  try {
    const physicalSection = sectionResults.value.find(
      (s) => s?.pageName === 'physical_questions'
    );
    
    if (!physicalSection || !physicalSection.factors || index >= physicalSection.factors.length) {
      return '';
    }
    
    return physicalSection.factors[index]?.value ? 'scale-150' : '';
  } catch (error) {
    console.error('Error in highlightSwatch:', error);
    return '';
  }
};

const currentQuestion = ref('');
</script>

<template>
  <div class="px-8 pt-6 gap-y-4 w-full">
    <div class="flex items-center gap-x-4 mt-2 mb-1">
      <h3 class="m-0 font-light">Sammanfattning Fysisk aktivitet</h3>
    </div>

    <div v-if="!questionsAnswered" class="mt-4 text-base">
      Du har inte svarat på de andra frågorna. 
    </div>

    <p v-else-if="fmsType === FmsType.Work" class="w-full mt-0 text-base">
      Medelvärde av min kondition, fysiska träning, vardagsaktiviteter, stillasittande.
    </p>
    <p v-else class="w-full mt-0 text-base">
      Medelvärde av fysiska träning, vardagsaktiviteter, stillasittande.
    </p>

    <div
      v-if="questionsAnswered && factor"
      :class="borderColor"
      class="border-t border-0 border-solid py-4 text-base"
    >
      <div>Du har {{ factor?.label === 'okänd' ? 'okänt' : (factor?.label || '') }} värde.</div>

      <ul class="flex flex-col m-0 mt-4 p-0">
        <li class="list-none">
          <b-radio
            :name="`physicalActivity-improve`"
            :color="colorScheme"
            value="1"
            label="Detta är jag nöjd med"
            :label-id="`physicalActivity-improve-1-label`"
            @change="handleImprove('1')"
            v-model="improve"
          />
        </li>

        <li class="list-none">
          <b-radio
            :name="`physicalActivity-improve`"
            :color="colorScheme"
            value="2"
            label="Detta vill jag förbättra"
            :label-id="`physicalActivity-improve-2-label`"
            @change="handleImprove('2')"
            v-model="improve"
          />
        </li>
      </ul>

      <div class="w-full flex justify-center">
        <div
          class="flex justify-center space-x-2 items-center text-base mb-24 mt-10 w-full max-w-md"
        >
          <div class="flex-1 flex text-sm items-center">
            <div
              class="flex-1 border-0 border-t border-solid border-black/30"
            ></div>
            <div class="px-1">Risk</div>
          </div>
          <div class="flex flex-col items-center">
            <div class="flex space-x-2">
              <span
                :class="[
                  'flex items-center justify-center w-5 h-5 outline outline-1 rounded-full text-sm transition border',
                  bgColors[1],
                  currentValue === '1' ? 'scale-150' : '',
                ]"
                >1</span
              >
              <span
                :class="[
                  'flex items-center justify-center w-5 h-5 outline outline-1 rounded-full text-sm transition',
                  bgColors[2],
                  currentValue === '2' ? 'scale-150' : '',
                ]"
                >2</span
              >
              <span
                :class="[
                  'flex items-center justify-center w-5 h-5 outline outline-1 rounded-full text-sm transition',
                  bgColors[3],
                  currentValue === '3' ? 'scale-150' : '',
                ]"
                >3</span
              >
              <span
                :class="[
                  'flex items-center justify-center w-5 h-5 outline outline-1 rounded-full text-sm transition',
                  bgColors[4],
                  currentValue === '4' ? 'scale-150' : '',
                ]"
                >4</span
              >
              <span
                :class="[
                  'flex items-center justify-center w-5 h-5 outline outline-1 rounded-full text-sm transition',
                  bgColors[5],
                  currentValue === '5' ? 'scale-150' : '',
                ]"
                >5</span
              >
            </div>
          </div>
          <div class="flex-1 flex text-sm items-center">
            <div class="px-1">Frisk</div>
            <div
              class="flex-1 border-0 border-t border-solid border-black/30"
            ></div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>